import { useCallback, useEffect, useState } from "react";
import { Coins, CreditCard, PlayCircle, LockKeyhole, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import {
  claimAdReward,
  createPaymentOrder,
  getBillingCatalog,
  getMe,
  getPersonalOffers,
  getPaymentOrderStatus,
  getWallet,
  mergeWalletEarnings,
  redeemWalletEarnings,
  verifyPaymentOrder,
} from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { setUser } from "@/lib/storage";

export default function Wallet({ embedded = false }) {
  const [wallet, setWallet] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [user, setCurrentUser] = useState(null);
  const [busyProduct, setBusyProduct] = useState(null);
  const [pendingPayment, setPendingPayment] = useState(null);
  const [offers, setOffers] = useState([]);
  const [subscriptionDiscounts, setSubscriptionDiscounts] = useState({
    pro: { couponCode: ["pro", "all_paid"].includes(localStorage.getItem("hm_creator_referral_tier")) ? localStorage.getItem("hm_creator_referral_code") || "" : "", offerId: "" },
    winter_arc: { couponCode: ["winter_arc", "all_paid"].includes(localStorage.getItem("hm_creator_referral_tier")) ? localStorage.getItem("hm_creator_referral_code") || "" : "", offerId: "" },
  });

  const proOfferFullyDiscounted = isFullyDiscountedPersonalOffer(
    offers,
    subscriptionDiscounts.pro.offerId,
    catalog?.currency,
  );
  const winterArcOfferFullyDiscounted = isFullyDiscountedPersonalOffer(
    offers,
    subscriptionDiscounts.winter_arc.offerId,
    catalog?.currency,
  );

  const refresh = useCallback(async () => {
    try {
      const [nextWallet, nextCatalog, nextUser, nextOffers] = await Promise.all([
        getWallet(),
        getBillingCatalog(),
        getMe(),
        getPersonalOffers().catch(() => ({ items: [] })),
      ]);
      setWallet(nextWallet);
      setCatalog(nextCatalog);
      setCurrentUser(nextUser);
      setOffers(nextOffers.items || []);
      setUser(nextUser);
    } catch (error) {
      toast("Could not load payments.", { description: error.message });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!pendingPayment) return undefined;
    let active = true;
    const check = async () => {
      try {
        const result = await getPaymentOrderStatus(pendingPayment.localOrderId);
        if (active && result.status === "paid") {
          setPendingPayment(null);
          await refresh();
          toast("Payment confirmed.", { description: "Your account has been updated." });
        }
      } catch (error) {
        if (active) toast("Could not check payment yet.", { description: error.message });
      }
    };
    const timer = window.setInterval(check, 3000);
    check();
    return () => { active = false; window.clearInterval(timer); };
  }, [pendingPayment, refresh]);

  const purchase = async (product, paymentMethod) => {
    setBusyProduct(product.code);
    try {
      const selectedDiscount = subscriptionDiscounts[product.kind] || {};
      const enteredCoupon = selectedDiscount.couponCode?.trim();
      const subscriptionDiscount = ["pro", "winter_arc"].includes(product.kind) ? {
        couponCode: enteredCoupon || undefined,
        offerId: enteredCoupon ? undefined : (selectedDiscount.offerId || undefined),
      } : {};
      const order = await createPaymentOrder(product.code, product.currency, paymentMethod, subscriptionDiscount);
      if (order.paymentMethod === "fully_discounted" && order.status === "paid") {
        await refresh();
        setSubscriptionDiscounts((current) => ({
          ...current,
          [product.kind]: { couponCode: "", offerId: "" },
        }));
        toast(product.kind === "winter_arc" ? "Winter Arc is active." : "Pro is active.", {
          description: "Your offer covered the full price. No payment was required.",
        });
        return;
      }
      if (order.paymentMethod !== "payment_page") {
        setPendingPayment({ ...order, label: product.label });
        if (order.paymentMethod === "payment_link") window.open(order.paymentUrl, "_blank", "noopener,noreferrer");
        return;
      }
      const checkoutResult = await openRazorpayCheckout(order);
      await verifyPaymentOrder(order.localOrderId, checkoutResult);
      await refresh();
      toast(product.kind === "winter_arc" ? "Winter Arc is active." : product.kind === "pro" ? "Pro is active." : "Credits added.", {
        description: product.label,
      });
    } catch (error) {
      if (error.code === "payment_pending_capture") {
        toast("Payment received.", { description: "Waiting for bank capture; your account will update automatically." });
      } else {
        toast("Payment was not completed.", { description: error.message });
      }
    } finally {
      setBusyProduct(null);
    }
  };

  const reward = async () => {
    try {
      await claimAdReward(`ad_${crypto.randomUUID()}`);
      await refresh();
      toast("Reward added.");
    } catch (error) {
      toast("Rewarded ads are not available yet.", { description: error.message });
    }
  };

  const mergeEarnings = async () => {
    if (!wallet.earnedTokens) return;
    try {
      await mergeWalletEarnings(wallet.earnedTokens);
      await refresh();
      toast("Earnings merged.", { description: "They are now available for future Sprint Pact stakes." });
    } catch (error) {
      toast("Could not merge earnings.", { description: error.message });
    }
  };

  const redeemEarnings = async () => {
    if (!wallet.earnedTokens) return;
    try {
      const redemption = await redeemWalletEarnings(wallet.earnedTokens);
      await refresh();
      toast(
        redemption.status === "provider_unknown" ? "Redemption needs reconciliation." : "Payout link created.",
        {
          description: redemption.status === "provider_unknown"
            ? "Your credits remain safely reserved; support must verify the provider outcome."
            : "RazorpayX sent the secure payout link to your account email.",
        },
      );
    } catch (error) {
      toast("Could not redeem earnings.", { description: error.message });
    }
  };

  if (!wallet || !catalog || !user) {
    const loading = <div className="px-6 py-10 text-sm text-[#A1A1AA]">Loading secure payments...</div>;
    return embedded ? loading : <AppShell>{loading}</AppShell>;
  }
  const stakesEnabled = Boolean(catalog.features?.partnershipStakes);

  const content = (
      <div className="px-6 sm:px-10 py-10 max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="overline mb-3">Membership & payments</div>
          <h1 className="font-serif-editorial text-4xl">Your <em className="italic text-[#F59E0B]">HustlrMate plan.</em></h1>
          <p className="text-sm text-[#A1A1AA] mt-3">Prices are shown automatically in {catalog.currency}. Checkout may include UPI apps such as Google Pay and Paytm when available.</p>
        </div>

        {catalog.razorpayMode === "test" && (catalog.hostedPaymentUrls?.proPaymentPage || catalog.hostedPaymentUrls?.proPaymentLink) && (
          <section className="hm-card mb-6 rounded-md border border-white/10 p-5">
            <div className="overline mb-2">Razorpay hosted previews · test mode</div>
            <p className="mb-4 text-xs text-[#A1A1AA]">Inspect the Dashboard-created Pro payment experiences below. Automatic membership activation is tested through the “Pay securely” flow on the plan cards.</p>
            <div className="flex flex-wrap gap-3">
              {catalog.hostedPaymentUrls?.proPaymentPage && <a href={catalog.hostedPaymentUrls.proPaymentPage} target="_blank" rel="noreferrer" className="hm-btn-ghost rounded-md px-4 py-2 text-xs">Open Pro payment page</a>}
              {catalog.hostedPaymentUrls?.proPaymentLink && <a href={catalog.hostedPaymentUrls.proPaymentLink} target="_blank" rel="noreferrer" className="hm-btn-ghost rounded-md px-4 py-2 text-xs">Open Pro payment link</a>}
            </div>
          </section>
        )}

        <div className={`grid gap-4 mb-8 ${stakesEnabled ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2"}`}>
          {stakesEnabled && <Balance label="Owned & spendable" value={`${wallet.availableTokens} credits`} icon={Coins} />}
          {stakesEnabled && <Balance label="Committed to stakes" value={`${wallet.lockedTokens} credits`} icon={LockKeyhole} />}
          {stakesEnabled && <Balance label="Earned from partners" value={`${wallet.earnedTokens} credits`} sub="Kept separate until you merge or redeem" icon={Coins} />}
          <Balance
            label="Account"
            value={user.subscriptionTier === "winter_arc" ? "Winter Arc" : user.subscriptionTier === "pro" ? "Pro" : "Free"}
            sub={user.subscriptionTier === "winter_arc"
              ? `Through Jan 1, 2027 · ${user.matchCreditsRemaining ?? 0} matches remaining`
              : user.subscriptionTier === "pro"
                ? `${user.matchCreditsRemaining ?? 0} matches remaining · no time expiry`
                : undefined}
            icon={Sparkles}
          />
        </div>

        {stakesEnabled && wallet.earnedTokens > 0 && (
          <section className="hm-card p-6 rounded-md mb-6 border border-emerald-500/20">
            <div className="overline mb-2">Partner earnings</div>
            <h2 className="font-serif-editorial text-2xl">You earned {wallet.earnedTokens} credits.</h2>
            <p className="text-sm text-[#A1A1AA] mt-2 mb-4">
              Merge them into your owned balance for future stakes
              {wallet.cashRedemptionEnabled && `, or redeem ${formatMoney(wallet.earnedTokens * wallet.cashRedemptionRateMinorPerCredit, wallet.cashRedemptionCurrency)} through a secure RazorpayX payout link`}.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={mergeEarnings} className="hm-btn-primary px-4 py-2 rounded-md text-sm">Merge all into owned credits</button>
              {wallet.cashRedemptionEnabled && wallet.earnedTokens >= wallet.cashRedemptionMinTokens && (
                <button onClick={redeemEarnings} className="hm-btn-ghost px-4 py-2 rounded-md text-sm">Redeem all earnings</button>
              )}
            </div>
          </section>
        )}

        {stakesEnabled && wallet.redemptions.length > 0 && (
          <section className="hm-card p-6 rounded-md mb-6">
            <h2 className="font-serif-editorial text-2xl mb-4">Earnings redemptions</h2>
            <div className="space-y-3">
              {wallet.redemptions.map((redemption) => (
                <div key={redemption.id} className="flex flex-wrap justify-between gap-3 border-b border-white/5 pb-3 text-sm">
                  <div>
                    <div>{redemption.tokens} earned credits · {formatMoney(redemption.amountMinor, redemption.currency)}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#7a7a80] mt-1">{redemption.status.replace("_", " ")}</div>
                  </div>
                  {redemption.shortUrl && ["issued", "pending"].includes(redemption.status) && (
                    <a href={redemption.shortUrl} target="_blank" rel="noreferrer" className="hm-btn-ghost px-3 py-2 rounded-md text-xs">Complete payout</a>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="hm-card p-6 rounded-md mb-6">
          <SubscriptionDiscountFields
            tier="pro"
            currency={catalog.currency}
            offers={offers}
            value={subscriptionDiscounts.pro}
            onChange={(value) => setSubscriptionDiscounts((current) => ({ ...current, pro: value }))}
          />
          <div className="flex gap-3 items-center mb-5">
            <Sparkles className="w-5 h-5 text-[#F59E0B]" />
            <div>
              <h2 className="font-serif-editorial text-2xl">{user.subscriptionTier === "winter_arc" ? "Add four more matches" : user.subscriptionTier === "pro" ? "Refill Pro matches" : "Get Pro"}</h2>
              <p className="text-xs text-[#A1A1AA] mt-1">Each purchase adds four new matches. Pro itself has no time-based expiry; Winter Arc members keep their seasonal membership.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {catalog.proPlans.map((plan) => (
              <ProductCard
                key={plan.code}
                product={plan}
                busy={busyProduct === plan.code}
                onBuy={purchase}
                paymentMethods={catalog.paymentMethods}
                paymentsEnabled={catalog.paymentsEnabled}
                discountSelected={Boolean(subscriptionDiscounts.pro.couponCode.trim() || subscriptionDiscounts.pro.offerId)}
                fullyDiscounted={proOfferFullyDiscounted}
              />
            ))}
          </div>
        </section>

        {catalog.features?.winterArc && (
          <section className="border-2 border-white bg-black p-6 mb-6">
            <SubscriptionDiscountFields
              tier="winter_arc"
              currency={catalog.currency}
              offers={offers}
              value={subscriptionDiscounts.winter_arc}
              onChange={(value) => setSubscriptionDiscounts((current) => ({ ...current, winter_arc: value }))}
            />
            <div className="flex gap-3 items-center mb-5"><Sparkles className="w-5 h-5 text-white"/><div><h2 className="font-serif-editorial text-2xl">{user.subscriptionTier === "winter_arc" ? "Add Winter Arc matches" : "Enter Winter Arc"}</h2><p className="text-xs text-white/50 mt-1">Active through January 1, 2027. Includes four matches and remains active even after all four are used.</p></div></div>
            <div className="grid sm:grid-cols-2 gap-3">{(catalog.winterArcPlans || []).map((plan) => <ProductCard key={plan.code} product={plan} busy={busyProduct === plan.code} onBuy={purchase} paymentMethods={catalog.paymentMethods} paymentsEnabled={catalog.paymentsEnabled} discountSelected={Boolean(subscriptionDiscounts.winter_arc.couponCode.trim() || subscriptionDiscounts.winter_arc.offerId)} fullyDiscounted={winterArcOfferFullyDiscounted} />)}</div>
          </section>
        )}

        {stakesEnabled && <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
          <section className="hm-card p-6 rounded-md">
            <div className="flex gap-3 items-center mb-5">
              <CreditCard className="w-5 h-5 text-[#F59E0B]" />
              <div>
                <h2 className="font-serif-editorial text-2xl">Buy commitment credits</h2>
                <p className="text-xs text-[#A1A1AA] mt-1">Credits fund Sprint Pact stakes. Packs are not cash balances.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {catalog.creditPacks.map((pack) => (
                <ProductCard key={pack.code} product={pack} busy={busyProduct === pack.code} onBuy={purchase} paymentMethods={catalog.paymentMethods} paymentsEnabled={catalog.paymentsEnabled} compact />
              ))}
            </div>
          </section>

          <section className="hm-card p-6 rounded-md">
            <div className="flex gap-3 items-center mb-4">
              <PlayCircle className="w-5 h-5 text-[#F59E0B]" />
              <h2 className="font-serif-editorial text-2xl">Earn with ads</h2>
            </div>
            <p className="text-sm text-[#A1A1AA] mb-4">One verified rewarded ad grants {wallet.adRewardTokens} credits. Daily limit: {wallet.adDailyLimit}.</p>
            <button onClick={reward} className="hm-btn-ghost px-4 py-2 rounded-md text-sm">Watch rewarded ad</button>
          </section>
        </div>}

        {stakesEnabled && <section className="mt-6 hm-card p-6 rounded-md">
          <h2 className="font-serif-editorial text-2xl mb-4">Transactions</h2>
          {wallet.transactions.length ? (
            <div className="space-y-3">
              {wallet.transactions.map((item) => (
                <div key={item.id} className="flex justify-between gap-4 border-b border-white/5 pb-3 text-sm">
                  <span className="capitalize">{item.kind.replace("_", " ")}</span>
                  <div className="text-right">
                    <div className={item.tokenDelta >= 0 ? "text-emerald-300" : "text-[#F5F5F0]"}>
                      {item.tokenDelta >= 0 ? "+" : ""}{item.tokenDelta} credits
                    </div>
                    {item.amountMinor > 0 && <div className="text-[10px] text-[#7a7a80]">{formatMoney(item.amountMinor, item.currency)}</div>}
                    <div className="text-[9px] uppercase tracking-[0.14em] text-[#7a7a80]">{item.balanceBucket} balance</div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#A1A1AA]">No credit transactions yet.</p>}
        </section>}

        {pendingPayment && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4" role="dialog" aria-modal="true">
            <div className="hm-card w-full max-w-md rounded-md border border-white/20 p-6 text-center">
              <div className="overline mb-2">Secure Razorpay payment</div>
              <h2 className="font-serif-editorial text-2xl">{pendingPayment.label}</h2>
              {pendingPayment.paymentMethod === "qr_code" ? (
                <><img src={pendingPayment.paymentUrl} alt="Razorpay UPI payment QR code" className="mx-auto my-5 h-64 w-64 bg-white p-2" /><p className="text-sm text-[#A1A1AA]">Scan with any supported UPI app. This fixed-amount code expires after 15 minutes.</p></>
              ) : (
                <p className="my-5 text-sm text-[#A1A1AA]">Complete payment on Razorpay's hosted payment link, then return here. Confirmation is checked automatically.</p>
              )}
              <div className="mt-5 flex justify-center gap-3">
                {pendingPayment.paymentMethod === "payment_link" && <a href={pendingPayment.paymentUrl} target="_blank" rel="noreferrer" className="hm-btn-primary px-4 py-2 rounded-md text-sm">Open payment link</a>}
                <button onClick={() => setPendingPayment(null)} className="hm-btn-ghost px-4 py-2 rounded-md text-sm">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
  return embedded ? content : <AppShell>{content}</AppShell>;
}

function SubscriptionDiscountFields({ tier, currency, offers, value, onChange }) {
  const eligibleOffers = offers.filter((offer) => (
    [tier, "all_paid"].includes(offer.applicableTier)
    && (offer.discountType !== "fixed_price" || offer.currency === currency)
  ));
  const planName = tier === "winter_arc" ? "Winter Arc" : "Pro";

  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-2">
      <label className="block">
        <span className="text-[10px] uppercase tracking-[.16em] text-[#A1A1AA]">{planName} coupon code</span>
        <input
          value={value.couponCode}
          onChange={(event) => onChange({ couponCode: event.target.value.toUpperCase(), offerId: "" })}
          placeholder="CREATOR20"
          className="hm-input mt-2 w-full rounded-md px-3 py-2.5 text-sm uppercase"
        />
      </label>
      <label className="block">
        <span className="text-[10px] uppercase tracking-[.16em] text-[#A1A1AA]">{planName} personal offer</span>
        <select
          value={eligibleOffers.some((offer) => offer.id === value.offerId) ? value.offerId : ""}
          onChange={(event) => onChange({ couponCode: "", offerId: event.target.value })}
          className="mt-2 w-full rounded-md border border-white/10 bg-black px-3 py-2.5 text-sm"
        >
          <option value="">No personal offer</option>
          {eligibleOffers.map((offer) => (
            <option key={offer.id} value={offer.id}>
              {offer.name} · {offer.discountType === "percentage" ? `${offer.discountValue}% off` : `${formatMoney(offer.discountValue, offer.currency)} final price`}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function isFullyDiscountedPersonalOffer(offers, offerId, currency) {
  if (!offerId) return false;
  const offer = offers.find((item) => item.id === offerId);
  if (!offer) return false;
  if (offer.discountType === "percentage") return Number(offer.discountValue) === 100;
  return offer.discountType === "fixed_price"
    && offer.currency === currency
    && Number(offer.discountValue) === 0;
}

function ProductCard({ product, busy, onBuy, paymentMethods = [], paymentsEnabled = false, discountSelected = false, fullyDiscounted = false, compact = false }) {
  const supportedMethods = paymentMethods.filter((method) => method !== "qr_code" || product.currency === "INR");
  const [paymentMethod, setPaymentMethod] = useState(supportedMethods[0] || "payment_page");
  const checkoutEnabled = fullyDiscounted || ((paymentsEnabled || discountSelected) && supportedMethods.length > 0);
  return (
    <div className="p-4 rounded-md border border-white/10 bg-[#0f0f0f]">
      <div className={`${compact ? "text-sm" : "font-serif-editorial text-xl"}`}>{product.label}</div>
      {fullyDiscounted ? (
        <div className="mt-2">
          <div className="text-xs text-[#A1A1AA] line-through">{formatMoney(product.amountMinor, product.currency)}</div>
          <div className="font-serif-editorial text-2xl text-emerald-300">{formatMoney(0, product.currency)}</div>
          <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-emerald-300/80">Covered by your personal offer</p>
        </div>
      ) : (
        <div className="font-serif-editorial text-2xl text-[#F59E0B] mt-2">
          {formatMoney(product.amountMinor, product.currency)}
        </div>
      )}
      {!fullyDiscounted && (
        <>
          <label className="mt-4 block text-[10px] uppercase tracking-[0.14em] text-[#A1A1AA]">Payment method</label>
          <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} className="mt-2 w-full rounded-md border border-white/10 bg-black px-3 py-2 text-xs">
            {supportedMethods.includes("payment_page") && <option value="payment_page">Razorpay Standard Checkout</option>}
            {supportedMethods.includes("payment_link") && <option value="payment_link">Payment link</option>}
            {supportedMethods.includes("qr_code") && <option value="qr_code">UPI QR code</option>}
          </select>
        </>
      )}
      <button
        onClick={() => onBuy(product, paymentMethod)}
        disabled={busy || !checkoutEnabled}
        className="hm-btn-primary w-full px-3 py-2 rounded-md text-xs mt-4 disabled:opacity-50"
      >
        {busy ? (fullyDiscounted ? "Redeeming..." : "Opening...") : fullyDiscounted ? "Redeem offer" : paymentsEnabled ? "Pay securely" : discountSelected ? "Apply offer" : "Payments unavailable"}
      </button>
    </div>
  );
}

function Balance({ label, value, sub, icon: Icon }) {
  return (
    <div className="hm-card p-5 rounded-md">
      <Icon className="w-4 h-4 text-[#F59E0B] mb-3" />
      <div className="text-xs text-[#A1A1AA]">{label}</div>
      <div className="font-serif-editorial text-2xl mt-1">{value}</div>
      {sub && <div className="text-xs text-[#A1A1AA] mt-1">{sub}</div>}
    </div>
  );
}
